import { OfferNetworkProvider } from "@redotech/crdb-enums/marketing/offer-network-provider";
import { ResolvedOfferConfig } from "@redotech/order-tracking-common/offer-network/offer-placement";
import { OfferNetworkAttributes, toRoktAttributes } from "./offer-attributes";
import { OfferPlacementHandle } from "./offer-placement";
import { loadScriptOnce } from "./script-loader";

const ROKT_LAUNCHER_URL =
  "https://apps.rokt-api.com/wsdk/integrations/launcher.js";

/** The container id Rokt's SDK renders offers into. */
export const ROKT_PLACEHOLDER_ID = "rokt-placeholder";

interface RoktSelection {
  close?: () => void;
}

interface RoktLauncher {
  selectPlacements(options: {
    attributes: Record<string, string>;
    identifier: string;
  }): Promise<RoktSelection>;
}

interface RoktSdk {
  createLauncher(options: {
    accountId: string;
    sandbox?: boolean;
  }): Promise<RoktLauncher>;
}

declare global {
  interface Window {
    /** Pre-init resolver queue (array) until the launcher script replaces it. */
    Rokt?: RoktSdk | (() => void)[];
  }
}

const launchers = new Map<string, Promise<RoktLauncher>>();

function waitForRoktSdk(): Promise<RoktSdk> {
  return new Promise((resolve) => {
    const sdk = window.Rokt;
    if (sdk && !Array.isArray(sdk)) {
      resolve(sdk);
      return;
    }
    const queue = (window.Rokt = Array.isArray(window.Rokt) ? window.Rokt : []);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    queue.push(() => resolve(window.Rokt as RoktSdk));
  });
}

function getLauncher(
  accountId: string,
  sandbox: boolean,
): Promise<RoktLauncher> {
  const key = `${accountId}:${sandbox}`;
  let launcher = launchers.get(key);
  if (!launcher) {
    launcher = (async () => {
      await loadScriptOnce(ROKT_LAUNCHER_URL, {
        type: "module",
        crossOrigin: "anonymous",
      });
      const sdk = await waitForRoktSdk();
      return sdk.createLauncher({ accountId, sandbox });
    })();
    launcher.catch(() => launchers.delete(key));
    launchers.set(key, launcher);
  }
  return launcher;
}

export async function mountRoktPlacement(
  config: Extract<ResolvedOfferConfig, { provider: OfferNetworkProvider.Rokt }>,
  attributes: OfferNetworkAttributes,
): Promise<OfferPlacementHandle> {
  const launcher = await getLauncher(config.accountId, config.sandbox);
  const selection = await launcher.selectPlacements({
    attributes: toRoktAttributes(attributes),
    identifier: config.pageIdentifier,
  });
  return {
    dispose: () => {
      try {
        selection.close?.();
      } catch {
        // Never let SDK teardown break the page
      }
    },
  };
}
